import React, { useState, useEffect } from 'react';
import { channelsAPI, usersAPI } from '../../services/api';
import { toast } from 'react-toastify';
import Table from '../../components/common/Table';
import Modal from '../../components/common/Modal';
import Loading from '../../components/common/Loading';
import { useForm } from 'react-hook-form';

const ChannelsList = () => {
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);

  const { register, handleSubmit, reset, watch, setValue } = useForm();
  const channelType = watch('type');

  useEffect(() => {
    fetchChannels();
    fetchUsers();
  }, [search]);

  const fetchChannels = async () => {
    try {
      setLoading(true);
      const response = await channelsAPI.getAll({ search, limit: 100 });
      setChannels(response.data.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des chaînes');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll({ limit: 100, role: 'user' }); // Filtrer seulement les users (pas les admins)
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreate = () => {
    setEditingChannel(null);
    setSelectedUsers([]);
    reset();
    setModalOpen(true);
  };

  const handleEdit = (channel) => {
    setEditingChannel(channel);
    // Gérer managedBy comme tableau
    const managedByIds = Array.isArray(channel.managedBy) 
      ? channel.managedBy.map(u => u._id || u)
      : channel.managedBy?._id || channel.managedBy 
        ? [channel.managedBy._id || channel.managedBy] 
        : [];
    
    setSelectedUsers(managedByIds);
    
    reset({
      ...channel,
      managedBy: managedByIds,
      // Utiliser url ou youtubeUrl selon le type
      url: channel.type === 'youtube' ? channel.youtubeUrl : channel.url,
      otherType: channel.otherType || '',
    });
    setModalOpen(true);
  };

  const handleDelete = async (channel) => {
    if (!window.confirm(`Supprimer la chaîne ${channel.name}?`)) return;

    try {
      await channelsAPI.delete(channel._id || channel.id);
      toast.success('Chaîne supprimée');
      fetchChannels();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => {
      const newSelection = prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId];
      setValue('managedBy', newSelection);
      return newSelection;
    });
  };

  const onSubmit = async (data) => {
    try {
      // Préparer les données selon le type
      const channelData = {
        ...data,
        managedBy: selectedUsers.length > 0 ? selectedUsers : []
      };

      // Gérer l'URL selon le type
      if (data.type === 'youtube') {
        channelData.youtubeUrl = data.url || data.youtubeUrl;
        delete channelData.url;
      } else {
        channelData.url = data.url || data.youtubeUrl;
        delete channelData.youtubeUrl;
      }

      if (editingChannel) {
        await channelsAPI.update(editingChannel._id || editingChannel.id, channelData);
        toast.success('Chaîne modifiée');
      } else {
        await channelsAPI.create(channelData);
        toast.success('Chaîne créée');
      }
      setModalOpen(false);
      setSelectedUsers([]);
      fetchChannels();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur');
    }
  };

  const columns = [
    { header: 'Nom', accessor: 'name' },
    {
      header: 'Type',
      accessor: 'type',
      render: (channel) => {
        const typeLabels = {
          youtube: '📺 YouTube',
          tiktok: '🎵 TikTok',
          instagram: '📷 Instagram',
          facebook: '👥 Facebook',
          other: channel.otherType ? `🌐 ${channel.otherType}` : '🌐 Autre'
        };
        return (
          <span style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: 600,
            background: channel.type === 'youtube' ? '#FF0000' : 
                        channel.type === 'tiktok' ? '#000000' :
                        channel.type === 'instagram' ? '#E4405F' :
                        channel.type === 'facebook' ? '#1877F2' : '#6B7280',
            color: 'white',
          }}>
            {typeLabels[channel.type] || channel.type}
          </span>
        );
      },
    },
    {
      header: 'URL',
      accessor: 'url',
      render: (channel) => {
        const url = channel.youtubeUrl || channel.url;
        return url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--primary)', textDecoration: 'none' }}
          >
            {url.length > 50 ? url.substring(0, 50) + '...' : url}
          </a>
        ) : '-';
      },
    },
    { header: 'Description', accessor: 'description' },
    { header: 'Abonnés', accessor: 'subscribers' },
    {
      header: 'Géré par',
      accessor: 'managedBy',
      render: (channel) => {
        const managers = Array.isArray(channel.managedBy) 
          ? channel.managedBy 
          : channel.managedBy 
            ? [channel.managedBy] 
            : [];
        
        if (managers.length === 0) return '-';
        if (managers.length === 1) {
          const user = managers[0];
          return user.fullName || user.email || '-';
        }
        return `${managers.length} utilisateurs`;
      },
    },
  ];

  if (loading) return <Loading />;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Chaînes</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          ➕ Ajouter une chaîne
        </button>
      </div>

      <div style={styles.searchBar}>
        <input
          type="text"
          placeholder="Rechercher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      <Table
        columns={columns}
        data={channels}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingChannel ? 'Modifier la chaîne' : 'Nouvelle chaîne'}
      >
        <form onSubmit={handleSubmit(onSubmit)} style={styles.form}>
          <div style={styles.formGroup}>
            <label>Type de chaîne</label>
            <select {...register('type', { required: true })} className="input" defaultValue="youtube">
              <option value="youtube">📺 YouTube</option>
              <option value="tiktok">🎵 TikTok</option>
              <option value="instagram">📷 Instagram</option>
              <option value="facebook">👥 Facebook</option>
              <option value="other">🌐 Autre</option>
            </select>
          </div>
          {channelType === 'other' && (
            <div style={styles.formGroup}>
              <label>Type personnalisé</label>
              <input 
                {...register('otherType', { required: channelType === 'other' })} 
                className="input" 
                placeholder="Ex: LinkedIn, Twitter, Snapchat, etc."
              />
              <small style={styles.helpText}>
                Entrez le type de chaîne personnalisé
              </small>
            </div>
          )}
          <div style={styles.formGroup}>
            <label>Nom</label>
            <input {...register('name', { required: true })} className="input" />
          </div>
          <div style={styles.formGroup}>
            <label id="url-label">
              {channelType === 'youtube' ? 'URL YouTube' : 
               channelType === 'tiktok' ? 'URL TikTok' :
               channelType === 'instagram' ? 'URL Instagram' :
               channelType === 'facebook' ? 'URL Facebook' : 'URL'}
            </label>
            <input 
              {...register('url', { required: true })} 
              className="input" 
              placeholder={
                channelType === 'youtube' ? 'https://www.youtube.com/channel/...' :
                channelType === 'tiktok' ? 'https://www.tiktok.com/@...' :
                channelType === 'instagram' ? 'https://www.instagram.com/...' :
                channelType === 'facebook' ? 'https://www.facebook.com/...' :
                'https://...'
              }
            />
            <small style={styles.helpText}>
              Entrez l'URL de la chaîne selon le type sélectionné
            </small>
          </div>
          <div style={styles.formGroup}>
            <label>Description</label>
            <textarea
              {...register('description')}
              className="input"
              rows="4"
            />
          </div>
          <div style={styles.formGroup}>
            <label>Abonnés</label>
            <input
              type="number"
              {...register('subscribers', { valueAsNumber: true })}
              className="input"
              min="0"
            />
          </div>
          <div style={styles.formGroup}>
            <label>Géré par (utilisateurs)</label>
            <div style={styles.userList}>
              {users.length === 0 ? (
                <p style={styles.noUsers}>Aucun utilisateur disponible</p>
              ) : (
                users.map((user) => {
                  const userId = user._id || user.id;
                  const isSelected = selectedUsers.includes(userId);
                  return (
                    <label
                      key={userId}
                      style={{
                        ...styles.userItem,
                        ...(isSelected ? styles.userItemSelected : {})
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleUserToggle(userId)}
                        style={styles.checkbox}
                      />
                      <span>
                        {user.fullName} ({user.email})
                      </span>
                    </label>
                  );
                })
              )}
            </div>
            <small style={styles.helpText}>
              Sélectionnez un ou plusieurs utilisateurs. Les admins ne sont pas affichés dans cette liste.
            </small>
            <input type="hidden" {...register('managedBy')} />
          </div>
          <div style={styles.formActions}>
            <button type="button" onClick={() => setModalOpen(false)} className="btn btn-outline">
              Annuler
            </button>
            <button type="submit" className="btn btn-primary">
              {editingChannel ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 700,
    color: 'var(--text)',
  },
  searchBar: {
    marginBottom: '1.5rem',
  },
  searchInput: {
    width: '100%',
    maxWidth: '400px',
    padding: '0.75rem',
    border: '1px solid var(--border)',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  formActions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
    marginTop: '1rem',
  },
  helpText: {
    fontSize: '0.75rem',
    color: 'var(--text-light)',
    marginTop: '0.25rem',
  },
  userList: {
    border: '1px solid var(--border)',
    borderRadius: '0.5rem',
    padding: '0.5rem',
    maxHeight: '200px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  userItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  userItemSelected: {
    background: 'var(--light)',
    border: '1px solid var(--primary)',
  },
  checkbox: {
    cursor: 'pointer',
  },
  noUsers: {
    padding: '1rem',
    textAlign: 'center',
    color: 'var(--text-light)',
    fontSize: '0.875rem',
  },
};

export default ChannelsList;








